import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { RequestComboReports } from '@app/users/model/users-model';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  PaymentCertReportsDataResponseDTO,
  PaymentCertReportsRequestDTO,
  UserByCaseRequestDTO,
} from '../model/payment-model';
import { MatTableDataSource } from '@angular/material/table';
import { dataPrint } from '@app/requests/model/requests-model';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import { DatePipe } from '@angular/common';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import FileSaver from 'file-saver';
import { PaymentCertApi } from '../services/paymentCert.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { Credentials } from '@app/@core/models/login-models';
import { COMMERCIAL_EXTERNAL, ESTABLISHMENT, PRESCRIBER, VENDOR } from '@app/@core/constants/users.constants';
import { CredentialsService } from '@app/login';

const FORM_PARAM_CHAIN = 'chain';
const FORM_PARAM_ESTABLISHMENT = 'establishment';
const FORM_PARAM_PRESCRIPTOR = 'prescriptor';

const SUCCESSFULL_CODE_REGISTER_PAYMENT = '000';

@Component({
  selector: 'app-payment-history',
  templateUrl: './payment-history.component.html',
  styleUrls: ['./payment-history.component.scss'],
})
export class PaymentHistoryComponent implements OnInit {
  @Output() establishment = new EventEmitter<CommonDataDTO>();
  @Output() chain = new EventEmitter<CommonDataDTO>();
  @Output() prescriptor = new EventEmitter<CommonDataDTO>();
  @Output() status = new EventEmitter<CommonDataDTO>();

  public searchPaymentCert: FormGroup;

  chainsList: CommonDataDTO[];
  prescriptorsList: CommonDataDTO[];
  establishmentsList: CommonDataDTO[];

  public reports: PaymentCertReportsDataResponseDTO[] = null;

  public displayedColumns: string[] = ['date', 'amount', 'state'];

  public head: string[][];

  public dataSource = new MatTableDataSource<PaymentCertReportsDataResponseDTO>();

  isOperationOptional = false;
  isNumberContractOptional = false;
  private userInfo: Credentials;
  isExternalCommercial: boolean;

  constructor(
    readonly loaderGenericService: LoaderGenericService,
    readonly formBuilder: FormBuilder,
    readonly translateService: TranslateService,
    readonly customValidators: CustomValidatorsService,
    readonly combosStore: CombosStore,
    readonly excelService: ExcelService,
    readonly paymentServices: PaymentCertApi,
    readonly datePipe: DatePipe,
    readonly dialog: MatDialog,
    readonly cdref: ChangeDetectorRef,
    readonly credentialsService: CredentialsService
  ) {
    this.userInfo = this.credentialsService.credentials;
  }

  ngOnInit(): void {
    this.searchPaymentCert = this.formBuilder.group({
      chain: [{ value: '', disabled: false }, Validators.required],
      prescriptor: [{ value: '', disabled: false }, Validators.required],
      establishment: [{ value: '', disabled: false }, Validators.required],
      numberContract: [{ value: '', disabled: true }, [Validators.required, this.customValidators.isContractValid]],
      operation: [{ value: '', disabled: true }, [Validators.required, this.customValidators.isSolValid]],
    });
    this.loadHead();
    this.isExternalCommercial = this.userInfo.userType === COMMERCIAL_EXTERNAL;
    if (this.isExternalCommercial) {
      this.loadEstablishmentFromCommercialExternal();
      this.searchPaymentCert = this.formBuilder.group({
        establishment: [{ value: '', disabled: false }, Validators.required],
        numberContract: [{ value: '', disabled: true }, [Validators.required, this.customValidators.isContractValid]],
        operation: [{ value: '', disabled: true }, [Validators.required, this.customValidators.isSolValid]],
      });
    } else {
      this.loadChainsCombo();
    }
    this.translateService.onLangChange.subscribe(() => {
      this.loadHead();
    });
  }

  loadHead() {
    this.head = [
      [
        this.translateService.instant('paymentCert.date'),
        this.translateService.instant('paymentCert.import'),
        this.translateService.instant('paymentCert.state'),
      ],
    ];
  }

  numberOnly(event: KeyboardEvent | ClipboardEvent, element: HTMLInputElement): boolean {
    if (event instanceof KeyboardEvent && event.type === 'keypress') {
      const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');
      const value = this.searchPaymentCert.controls[inputForm].value;
      return this.customValidators.numberOnly(event.key.charCodeAt(0), value);
    } else if (event instanceof ClipboardEvent && event.type === 'paste') {
      const value = event.clipboardData?.getData('text') || '';
      for (const char of value) {
        if (!this.customValidators.numberOnly(char.charCodeAt(0), char)) {
          return false;
        }
      }
    }
  }

  readonly setValueSelect = (formControlName: string, array: CommonDataDTO[]) => {
    if (array?.length > 0 && array.length === 1) {
      this.searchPaymentCert.controls[formControlName]?.setValue(array[0]?.id);
      if (formControlName === FORM_PARAM_ESTABLISHMENT) {
        this.establishment.emit(this.searchPaymentCert.controls.establishment.value);
        this.searchPaymentCert.get('operation').enable();
        this.searchPaymentCert.get('numberContract').enable();
      } else if (formControlName === FORM_PARAM_CHAIN) {
        this.chain.emit(this.searchPaymentCert.controls.chain.value);
        this.searchPaymentCert.get('prescriptor').enable();
        this.loadPrescribersByChains();
      } else if (formControlName === FORM_PARAM_PRESCRIPTOR) {
        this.prescriptor.emit(this.searchPaymentCert.controls.prescriptor.value);
        this.searchPaymentCert.get('establishment').enable();
        this.loadEstablishmentComboByPrescribers();
      }
    }
  };

  readonly loadChainsCombo = async (filter?: UserByCaseRequestDTO) => {
    this.loaderGenericService.showSpinner();
    this.resetFormControlAndDataList(FORM_PARAM_CHAIN);
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.combosStore
      .getChains(filter)
      .then(
        (result) => {
          if (result?.length > 0) {
            this.chainsList = result;
            this.setValueSelect(FORM_PARAM_CHAIN, this.chainsList);
          }
        },
        (error) => console.error(error)
      )
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.loaderGenericService.stopSpinner();
      });
  };

  readonly loadPrescribersByChains = async () => {
    this.loaderGenericService.showSpinner();
    this.resetFormControlAndDataList(FORM_PARAM_PRESCRIPTOR);
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.searchPaymentCert.get('prescriptor').enable();
    const request = {
      listChains: [this.searchPaymentCert.controls[FORM_PARAM_CHAIN].value],
    };
    if (request) {
      if (
        this.userInfo.userType === VENDOR ||
        this.userInfo.userType === ESTABLISHMENT ||
        this.userInfo.userType === PRESCRIBER
      ) {
        this.combosStore.getPrescribers({}).then(
          (result) => {
            if (result?.length > 0) {
              this.prescriptorsList = result;
              this.setValueSelect(FORM_PARAM_PRESCRIPTOR, this.prescriptorsList);
              this.loaderGenericService.stopSpinner();
            }
          },
          (error) => console.error(error)
        );
      } else {
        this.combosStore.getPrescribersByChains(request).then(
          (result) => {
            if (result?.length > 0) {
              this.prescriptorsList = result;
              this.setValueSelect(FORM_PARAM_PRESCRIPTOR, this.prescriptorsList);
              this.loaderGenericService.stopSpinner();
            }
          },
          (error) => {
            console.error(error);
          }
        );
      }
    }
  };

  onChainSelection(event: HTMLSelectElement) {
    this.searchPaymentCert.controls[FORM_PARAM_CHAIN].setValue(event.value);
    this.loadPrescribersByChains();
  }

  onPrescriberSelection(event: HTMLSelectElement) {
    this.searchPaymentCert.controls[FORM_PARAM_PRESCRIPTOR].setValue(event.value);
    this.loadEstablishmentComboByPrescribers();
  }

  onEstablishmentSelection(event: HTMLSelectElement) {
    this.searchPaymentCert.controls[FORM_PARAM_ESTABLISHMENT].setValue(event.value);
    this.searchPaymentCert.get('operation').enable();
    this.searchPaymentCert.get('numberContract').enable();
  }

  readonly resetFormControlAndDataList = (formControlName: string) => {
    const formControl = this.searchPaymentCert.controls[formControlName];
    if (formControl) {
      formControl.reset();
      switch (formControlName) {
        case FORM_PARAM_CHAIN:
          this.chainsList = [];
          break;
        case FORM_PARAM_PRESCRIPTOR:
          this.prescriptorsList = [];
          this.searchPaymentCert.get('prescriptor').disable();
          break;
        case FORM_PARAM_ESTABLISHMENT:
          this.establishmentsList = [];
          this.searchPaymentCert.get('establishment').disable();

          this.searchPaymentCert.controls.operation.setValue('');
          this.searchPaymentCert.get('operation').disable();

          this.searchPaymentCert.controls.numberContract.setValue('');
          this.searchPaymentCert.get('numberContract').disable();
          break;
        default:
          console.error('formControlName - NOT FOUND', formControlName);
      }
    }
  };

  readonly loadEstablishmentComboByPrescribers = async () => {
    this.loaderGenericService.showSpinner();
    this.resetFormControlAndDataList(FORM_PARAM_ESTABLISHMENT);
    this.searchPaymentCert.get('establishment').enable();
    const request: RequestComboReports = {
      listPrescribers: [this.searchPaymentCert.controls.prescriptor?.value],
      listChains: [this.searchPaymentCert.controls.chain?.value],
      officeId: this.searchPaymentCert.controls.macroOffice?.value,
    };
    if (request) {
      if (this.userInfo.userType === VENDOR || this.userInfo.userType === ESTABLISHMENT) {
        this.combosStore.getEstablishments({}).then(
          (result) => {
            if (result?.length > 0) {
              this.establishmentsList = result;
              this.setValueSelect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
              this.loaderGenericService.stopSpinner();
            }
          },
          (error) => console.error(error)
        );
      } else {
        this.combosStore
          .getEstablishmentsByPrescribers(request)
          .then(
            (result) => {
              if (result?.length > 0) {
                this.establishmentsList = result;
                this.setValueSelect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
                this.loaderGenericService.stopSpinner();
              }
            },
            (error) => {
              console.log(error);
            }
          )
          .catch((error) => {
            console.log(error);
          });
      }
    }
  };

  readonly loadEstablishmentFromCommercialExternal = async () => {
    let commercialDto = {
      commercialId: this.userInfo.userId,
    };
    this.combosStore.getEstablishmentWithCertifiedPaymentByExternalCommercial(commercialDto).then((result) => {
      if (result?.length > 0) {
        this.establishmentsList = result;
        this.setValueSelect(FORM_PARAM_ESTABLISHMENT, this.establishmentsList);
      }
    });
    this.searchPaymentCert.get('establishment').enable();
  };

  formatData(originalFormat: string): string {
    if (originalFormat.length !== 8) {
      return '';
    }

    const ano = originalFormat.substring(0, 4);
    const mes = originalFormat.substring(4, 6);
    const dia = originalFormat.substring(6, 8);

    return `${dia}/${mes}/${ano}`;
  }

  insertData(data: PaymentCertReportsDataResponseDTO[]) {
    this.reports = data; //for PFD and excel
    this.reports = this.reports.map((object) => ({
      //Change format data of array
      ...object,
      date: this.formatData(object.date),
    }));
    this.reports.sort((a, b) => {
      if (a.date === '') {
        return -1;
      } else if (b.date === '') {
        return 1;
      }
      const dateA = a.date.split('/').reverse().join('-');
      const dateB = b.date.split('/').reverse().join('-');
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
    this.dataSource.data = this.reports; //for table
  }

  public isOptional() {
    const field1 = this.searchPaymentCert.get('operation');
    const field2 = this.searchPaymentCert.get('numberContract');

    const field1HasValue = !!field1.value && field1.value.trim() !== '';
    const field2HasValue = !!field2.value && field2.value.trim() !== '';

    if ((field1HasValue && field2HasValue) || (!field1HasValue && !field2HasValue)) {
      // If both fields have values, both are required || If both are empty, both are required
      field1.setValidators([Validators.required, this.customValidators.isSolValid]);
      field2.setValidators([Validators.required, this.customValidators.isContractValid]);
      this.isNumberContractOptional = false;
      this.isOperationOptional = false;
    } else if (field1HasValue || field2HasValue) {
      // If one of the fields has a value, the other becomes optional
      field1.setValidators(field2HasValue ? [] : [Validators.required, this.customValidators.isSolValid]);
      field2.setValidators(field1HasValue ? [] : [Validators.required, this.customValidators.isContractValid]);
      if (field1HasValue) {
        this.isNumberContractOptional = true;
      } else {
        this.isOperationOptional = true;
      }
    }

    // Update the validity of both controls
    field1.updateValueAndValidity();
    field2.updateValueAndValidity();
  }

  public createExcel() {
    const allDataExcel: dataPrint = { headers: this.head[0], data: this.reports };
    const excelAllData = {
      data: allDataExcel,
      dateFrom: this.datePipe.transform(this.searchPaymentCert.controls.initialDate?.value, 'yyyy-MM-dd'),
      dateTo: this.datePipe.transform(this.searchPaymentCert.controls.finalDate?.value, 'yyyy-MM-dd'),
    };
    this.excelService.exportExcelPaymentCert(excelAllData);
  }

  public createPdf() {
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        format: [360, 209],
      });

      const baseName = this.translateService.instant('paymentCert.report');
      const date = moment().locale('es');
      const filename = baseName.replace(/\s+/g, '_') + date.format('DDMMYYYY');

      doc.setProperties({
        title: filename,
      });
      const dataColumns: string[] = ['date', 'amount', 'state'];
      const dataPdf: PaymentCertReportsDataResponseDTO[] = [];
      //HEAD ya declarado

      // BODY
      this.reports?.forEach((item) => {
        const dataPdfOrder = JSON.stringify(item, dataColumns);
        const ObjectDataPdf = JSON.parse(dataPdfOrder);
        dataPdf.push(ObjectDataPdf);
      });

      autoTable(doc, {
        startY: 55,
        head: this.head,
        headStyles: { fillColor: '#969696', fontSize: 8 },
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        margin: { top: 55 },
        body: dataPdf.map(Object.values),
        didDrawPage: (data) => {
          /* HEADER */
          doc.setFontSize(24);
          doc.setTextColor('1E90FF');
          doc.text(baseName, 160, 25);
          doc.setTextColor('#000000');
          doc.setFontSize(16);
          doc.text(baseName, 14, 52);
          doc.setFontSize(11);
          doc.text(
            `${this.translateService.instant('paymentCert.date')}: ${moment()
              .locale('es')
              .format('DD-MM-YYYY HH:mm:ss')}`,
            220,
            37
          );

          doc.text(this.translateService.instant('excel.pag') + ' ' + data.pageNumber, 220, 45);

          const img = new Image();
          img.src = 'assets/img/logo.PNG';
          doc.addImage(img, 'png', 3, 10, 80, 30);
        },
      });

      const blob = doc.output('blob');
      FileSaver.saveAs(blob, filename);
      window.open(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  }

  private showDialogError(error: string) {
    console.error(error);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    dialogConfig.data = { text: error, icon: 'icon-ic_circle_cross' };
    this.dialog.open(ResponseDialogComponent, dialogConfig);
  }

  private sendReportPayment(request: PaymentCertReportsRequestDTO) {
    this.loaderGenericService.showSpinner();
    this.paymentServices
      .getPaymentCertReport(request)
      .then(
        (data) => {
          this.loaderGenericService.stopSpinner();
          if (data.codError === SUCCESSFULL_CODE_REGISTER_PAYMENT) {
            this.insertData(data.certificateRecordDataDTOList);
            this.cdref.detectChanges();
          } else {
            this.loaderGenericService.stopSpinner();
            this.showDialogError(data.desError);
          }
        },
        (error) => {
          console.log(error);
        }
      )
      .catch((error) => console.error(error))
      .finally(() => {
        this.loaderGenericService.stopSpinner();
      });
  }

  onSearchClick() {
    const requestToSend: PaymentCertReportsRequestDTO = {
      establishmentId: this.searchPaymentCert.controls.establishment.value.slice(0, -1),
      prescriberId: this.searchPaymentCert.controls.prescriptor?.value.slice(0, -1),
      chainId: this.searchPaymentCert.controls.chain?.value.slice(0, -1),
      contractId: this.searchPaymentCert.controls.numberContract.value,
      numSol: this.searchPaymentCert.controls.operation.value,
    };

    this.sendReportPayment(requestToSend);
  }
}
